import React, { useState } from "react";
import { DisclosureType } from "@eave/shared/types/api";
import { ArrowRight32 } from "@carbon/icons-react";
import {
  Button,
  Checkbox,
  ComboBox,
  Form,
  FormGroup,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import { EditingState } from "web_modules/shared/BaseTable";
import getHandlers, { setValueOnEvent } from "web_modules/shared/FormHelpers/eventHandlers";
import Validations, { replaceNonASCIIChars } from "web_modules/shared/FormHelpers/validation";
import feePaidByOptions from "web_modules/forms/data/feePaidByOptions";
import feePaidToOptions from "web_modules/forms/data/feePaidToOptions";
import { ComputedValuesForFeeType } from "./FeeTable";

const styles = require("./styles.module.scss");

interface IFormFee {
  description?: string;
  estimated_amount?: string;
  mismo_type?: string;
  paid_before_closing?: boolean;
  paid_by_type?: string;
  paid_to_type?: string;
  recipient?: string;
  actual_amount?: string;
}

interface Props {
  computedFeeData: ComputedValuesForFeeType;
  disclosure: DisclosureType;
  editingMode: EditingState;
  formState?: IFormFee;
  updateFormState: Function;
  updateSelectedDisclosure: Function;
  recipientOptions: any;
}

const FeesModalForm: React.FC<Props> = ({
  computedFeeData,
  disclosure,
  editingMode,
  formState,
  updateFormState,
  updateSelectedDisclosure,
  recipientOptions,
}) => {
  const [invalidFields, updateInvalidFields] = useState([]);
  const {
    handleTextInputChange,
    handleCurrencyInputChange,
    handleSelectChange,
    handleComboBoxChange,
    handleCheckboxChange,
  } = getHandlers(formState, updateFormState, invalidFields, updateInvalidFields);

  // so that the user text input for "recipient" can be selected,
  // in addition to the initial dropdown options.
  const [freeFormRecipient, updateFreeFormRecipient] = useState("");

  const getFeePreviewSection = form => {
    if (!form.mismo_type) {
      return <span className={styles.feePreviewDataText}>Select a fee to view details.</span>;
    }

    const mismoType = form.mismo_type;
    const isEmptyValue = !computedFeeData || mismoType === undefined; // "-"
    const computedFeeValues = computedFeeData[mismoType];

    if (!computedFeeValues) {
      return (
        <span className={styles.feePreviewDataText}>Please select or enter a valid fee type.</span>
      );
    }

    const { tolerance, included_in_apr, le_section, cd_section } = computedFeeValues;

    return (
      <div className={styles.feePreviewData}>
        <div className={styles.feePreviewDatapoint}>
          <span>Tolerance</span>
          <span>{isEmptyValue ? "-" : tolerance}</span>
        </div>
        <div className={styles.feePreviewDatapoint}>
          <span>Included In APR?</span>
          <span>{isEmptyValue ? "-" : included_in_apr ? "Yes" : "No"}</span>
        </div>
        <div className={styles.feePreviewDatapoint}>
          <span>Section</span>
          <span>
            {/* If no type is chosen OR the computed fees data hasn't yet computed, show "-",
                  Otherwise, show corresponding le_section or cd_section value. */}
            {isEmptyValue
              ? "-"
              : disclosure === DisclosureType.LoanEstimate
              ? le_section || "-"
              : cd_section || "-"}
          </span>
        </div>
      </div>
    );
  };

  const handleComboBoxTextChange = (value, field) => {
    if (field === "recipient") {
      updateFreeFormRecipient(value);
    }

    const event = {
      target: {
        name: field,
        value: value || "",
      },
    };

    return handleTextInputChange(event, field, [Validations.IsRequired]);
  };

  const truthyAndUnique = (element, index, arr) => {
    if (!element) {
      return false;
    }

    // case-insensitive unique, allowing for flexibility in user input.

    const upperArr = arr.map(el => el.toUpperCase());
    const upperEl = element.toUpperCase();

    return upperArr.indexOf(upperEl) === index;
  };

  // initial recipient options, plus the user input text
  // as a ComboBox dropdown option, if not empty & not already in the list.

  // also filtering unique as some contacts may be the same, across roles,
  // eg title company and settlement company.

  const allRecipientOptions = [...recipientOptions, freeFormRecipient]
    .filter(truthyAndUnique)
    .map(r => ({ label: r, value: r }));

  const selectedComboOption = (options, field) => {
    if (!formState) {
      return null;
    }
    const selectedOption = options.find(r => r.value === formState[field]);
    return selectedOption === undefined ? null : selectedOption;
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = replaceNonASCIIChars(e.target.value);
    handleTextInputChange(e, "description", [Validations.IsValid]);
  };

  return (
    <>
      <Form className={styles.feeForm}>
        {editingMode === EditingState.CREATE && (
          <Button
            style={{ position: "absolute", top: "13px", left: "280px" }}
            size="small"
            kind="ghost"
            onClick={() => {
              updateSelectedDisclosure(
                disclosure === DisclosureType.LoanEstimate
                  ? DisclosureType.ClosingDisclosure
                  : DisclosureType.LoanEstimate,
              );
            }}
          >
            Switch to{" "}
            {disclosure === DisclosureType.LoanEstimate
              ? DisclosureType.ClosingDisclosure
              : DisclosureType.LoanEstimate}
          </Button>
        )}
        <FormGroup legendText="">
          <TextInput
            id="fee-description"
            name="description"
            value={formState?.description || ""}
            labelText="Description *"
            placeholder="Appraisal Fee"
            type="text"
            invalid={invalidFields.includes("description")}
            invalidText="Enter a valid fee description"
            onBlur={handleDescriptionChange}
            onChange={handleDescriptionChange}
            size="sm"
          />
          <div style={{ marginBottom: "10px" }} />

          <div className={styles.feePreviewContainer}>
            <ComboBox
              id="mismo-type"
              name="mismo_type"
              titleText="Fee Type *"
              placeholder="Fee Type"
              items={Object.keys(computedFeeData).map(fee => {
                // This map enables selectedComboOption() to successfully grab the value
                return { value: fee };
              })}
              initialSelectedItem={selectedComboOption(
                Object.keys(computedFeeData).map(f => {
                  return {
                    value: f,
                  };
                }),
                "mismo_type",
              )}
              itemToString={e => (e && e.value) || ""} // dictates the value of event.target.value, not just the display label in the dropdown
              onChange={e => handleComboBoxChange(e, "mismo_type", "")}
              onInputChange={e => handleComboBoxTextChange(e, "mismo_type")}
              invalid={invalidFields.includes("mismo_type")}
              invalidText="A valid value is required"
              helperText="Choose a fee type or type one in"
              light
              size="sm"
              style={{ minWidth: "300px" }}
            />
            <ArrowRight32 style={{ flexGrow: 1 }} />

            {getFeePreviewSection(formState)}
          </div>
          <div style={{ marginBottom: "10px" }}>
            <ComboBox
              id="fee-recipient"
              name="recipient"
              titleText="Fee Recipient *"
              placeholder="Recipient"
              items={allRecipientOptions}
              selectedItem={selectedComboOption(allRecipientOptions, "recipient")}
              itemToString={e => (e ? e.label : "")}
              onChange={e => handleComboBoxChange(e, "recipient", null)} // `recipient` is nullable in API
              onInputChange={e => handleComboBoxTextChange(e, "recipient")}
              downshiftProps={{
                selectedItemChanged: (prevItem, item) => {
                  const getValue = v => (v === null ? null : v.value);
                  return getValue(prevItem) !== getValue(item);
                },
              }}
              invalid={invalidFields.includes("recipient")}
              invalidText="A valid value is required"
              helperText="Choose a recipient or type one in"
              light
              size="sm"
            />
          </div>

          <Select
            id="fee-paid-by-type"
            name="paid_by_type"
            value={(formState && formState.paid_by_type) || ""}
            invalid={invalidFields.includes("paid_by_type")}
            invalidText="A valid value is required"
            labelText="Paid By Type*"
            onBlur={e => handleSelectChange(e, "paid_by_type", [Validations.IsValid])}
            onChange={e => handleSelectChange(e, "paid_by_type", [Validations.IsValid])}
            size="sm"
          >
            <SelectItem text="Select a Paid By Type..." value="" />
            {feePaidByOptions.map(paid_by_type => (
              <SelectItem
                key={`paidbytype-${paid_by_type.label}`}
                text={paid_by_type.label}
                value={paid_by_type.value}
              />
            ))}
          </Select>
          <div style={{ marginBottom: "10px" }} />
          <Select
            id="fee-paid-to-type"
            name="paid_to_type"
            value={(formState && formState.paid_to_type) || ""}
            invalid={invalidFields.includes("paid_to_type")}
            invalidText="A valid value is required"
            labelText="Paid To Type*"
            onBlur={e => handleTextInputChange(e, "paid_to_type", [Validations.IsValid])}
            onChange={e => handleTextInputChange(e, "paid_to_type", [Validations.IsValid])}
            size="sm"
          >
            <SelectItem text="Select a Paid To Type..." value="" />
            {feePaidToOptions.map(paid_to_type => (
              <SelectItem
                key={`paidtotype-${paid_to_type.label}`}
                text={paid_to_type.label}
                value={paid_to_type.value}
              />
            ))}
          </Select>
          <div style={{ marginBottom: "10px" }} />
          <TextInput
            id="fee-estimated-amount"
            name="estimated_amount"
            value={formState && formState.estimated_amount && `$${formState.estimated_amount}`}
            labelText="Final LE Amount ($) *"
            type="text"
            invalid={invalidFields.includes("estimated_amount")}
            invalidText="This is a required value. Please enter a number with up to 2 decimal places (eg -4.05, 1,000)"
            onChange={e => {
              handleCurrencyInputChange(e, "estimated_amount", [Validations.IsValid]);
            }}
            onBlur={e => {
              handleCurrencyInputChange(e, "estimated_amount", [
                Validations.IsValid,
                Validations.IsNonNull,
                Validations.IsDecimal,
              ]);
            }}
            size="sm"
            disabled={disclosure === DisclosureType.ClosingDisclosure}
          />
          <div style={{ marginBottom: "10px" }} />
          {/* Only CD's see the actual_amount */}
          {disclosure === DisclosureType.ClosingDisclosure && (
            <>
              <TextInput
                id="fee-actual-amount"
                name="actual_amount"
                value={
                  (formState && formState.actual_amount && `$${formState.actual_amount}`) || ""
                }
                labelText="Actual Amount ($) *"
                type="text"
                invalid={invalidFields.includes("actual_amount")}
                invalidText="Please enter a number with up to 2 decimal places (eg -4.05, 1,000)"
                onChange={e => {
                  let event = { ...e };
                  let tests = [];

                  // empty field = reset to null, giving Ops a way to unset field,
                  // distinguishing between 0 and unset.
                  if (event.target.value === "" || event.target.value === "$") {
                    event = setValueOnEvent(e, null);
                  } else {
                    // The value is not required, so do not require a non-null value by default.
                    // However, if they DO type something, ensure it's a decimal.
                    tests = [Validations.IsDecimal];
                  }

                  handleCurrencyInputChange(event, "actual_amount", tests);
                }}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
            </>
          )}
          <Checkbox
            id="fee-poc"
            name="paid_before_closing"
            labelText="Paid Before Closing? (POC)"
            checked={(formState && formState.paid_before_closing) || false}
            onChange={() =>
              handleCheckboxChange(formState.paid_before_closing, "paid_before_closing")
            }
          />
        </FormGroup>
      </Form>
    </>
  );
};

export default FeesModalForm;
